/**
 * Τρεις τόνοι ενός χρώματος για τα γραφήματα, υπολογισμένοι ανά θέμα (μεταφορά του δείγματος A,
 * dashboard_mock/_work/template.html → applyTheme):
 *   1 = brand-ink (το χρώμα ως κείμενο — ήδη ≥ 4.5:1 στη σελίδα, utils/storeTheme)
 *   3 = ο πιο ανοιχτός που κρατά 2:1 πάνω στο φύλλο (το «15%» χανόταν στο «Χαρτί»)
 *   2 = στη μέση της φωτεινότητας (OKLab L) ανάμεσα στο 1 και στο 3
 * Όλοι είναι μείξεις του ink με το χρώμα του φύλλου, άρα ίδια απόχρωση σε ανοιχτό και σκούρο.
 * Κόκκινο/πράσινο/πορτοκαλί ΔΕΝ μπαίνουν εδώ — μόνο στα Δ και στην «Προσοχή».
 */

export type Rgb = [number, number, number];

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export function hslToRgb(h: number, s: number, l: number): Rgb {
  const S = clamp(s, 0, 100) / 100;
  const L = clamp(l, 0, 100) / 100;
  const H = ((h % 360) + 360) % 360;
  const c = (1 - Math.abs(2 * L - 1)) * S;
  const x = c * (1 - Math.abs(((H / 60) % 2) - 1));
  const m = L - c / 2;
  const [r, g, b] = H < 60 ? [c, x, 0] : H < 120 ? [x, c, 0] : H < 180 ? [0, c, x] : H < 240 ? [0, x, c] : H < 300 ? [x, 0, c] : [c, 0, x];
  return [(r + m) * 255, (g + m) * 255, (b + m) * 255];
}

/**
 * Χρώμα από CSS: τριάδα token («224 64% 51%», όπως τα --brand/--surface-*), «#rrggbb», «rgb(…)»/«rgba(…)»
 * (όπως επιστρέφει το getComputedStyle), «hsl(…)». Άγνωστο/κενό → null.
 */
export function parseColor(input: string | null | undefined): Rgb | null {
  const s = String(input || '').trim();
  if (!s) return null;
  let m = /^#([0-9a-f]{6})$/i.exec(s);
  if (m) return [0, 2, 4].map(i => parseInt(m![1].slice(i, i + 2), 16)) as Rgb;
  m = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)\s*(?:[,/]\s*([\d.]+%?)\s*)?\)$/i.exec(s);
  if (m) {
    if (m[4] !== undefined && parseFloat(m[4]) === 0) return null; // διάφανο: δεν είναι «το φύλλο»
    return [Number(m[1]), Number(m[2]), Number(m[3])];
  }
  m = /^(?:hsla?\(\s*)?(-?[\d.]+)(?:deg)?[\s,]+([\d.]+)%[\s,]+([\d.]+)%\s*(?:[,/]\s*[\d.]+%?\s*)?\)?$/i.exec(s);
  if (m) return hslToRgb(Number(m[1]), Number(m[2]), Number(m[3]));
  return null;
}

export const toHex = (c: Rgb): string =>
  `#${c.map(v => Math.round(clamp(v, 0, 255)).toString(16).padStart(2, '0')).join('')}`;

/** p = μερίδιο του `a` (1 → a, 0 → b). */
export const mixRgb = (a: Rgb, b: Rgb, p: number): Rgb => [0, 1, 2].map(i => a[i] * p + b[i] * (1 - p)) as Rgb;

const lin = (c: number) => {
  const v = clamp(c, 0, 255) / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
};

export const relLuminance = (c: Rgb): number => 0.2126 * lin(c[0]) + 0.7152 * lin(c[1]) + 0.0722 * lin(c[2]);

export function contrast(a: Rgb, b: Rgb): number {
  const x = relLuminance(a);
  const y = relLuminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

/** Φωτεινότητα OKLab (0..1). */
export function oklabL(c: Rgb): number {
  const r = lin(c[0]);
  const g = lin(c[1]);
  const b = lin(c[2]);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return 0.2104542553 * l + 0.793617785 * m - 0.0040720453 * s;
}

/** Διχοτόμηση στο [0,1] για αύξουσα f: το p με f(p) ≈ target. */
export function solve(f: (p: number) => number, target: number): number {
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 30; i += 1) {
    const mid = (lo + hi) / 2;
    if (f(mid) < target) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/** Ελάχιστη αντίθεση του ανοιχτότερου τόνου πάνω στο φύλλο. */
export const LIGHTEST_CONTRAST = 2.05;

export interface VizTones {
  c1: string;
  c2: string;
  c3: string;
  /** Η «ράγα» πίσω από μπάρες μεριδίου. */
  track: string;
  /** Κείμενο πάνω σε κάθε τόνο: λευκό ή το text-1 του θέματος, ό,τι διαβάζεται καλύτερα. */
  on1: string;
  on2: string;
  on3: string;
  /** Μερίδιο του ink στους τόνους 2 και 3 (για έλεγχο). */
  p2: number;
  p3: number;
}

export function computeVizTones(ink: Rgb, sheet: Rgb, text1: Rgb, dark: boolean): VizTones {
  const p3 = contrast(ink, sheet) <= LIGHTEST_CONTRAST ? 1 : solve(p => contrast(mixRgb(ink, sheet, p), sheet), LIGHTEST_CONTRAST);
  const L1 = oklabL(ink);
  const L3 = oklabL(mixRgb(ink, sheet, p3));
  const Lm = (L1 + L3) / 2;
  const sgn = L1 > oklabL(sheet) ? 1 : -1;
  const p2 = p3 >= 1 ? 1 : solve(p => sgn * oklabL(mixRgb(ink, sheet, p)), sgn * Lm);
  const c1 = ink;
  const c2 = mixRgb(ink, sheet, p2);
  const c3 = mixRgb(ink, sheet, p3);
  const white: Rgb = [255, 255, 255];
  const onOf = (c: Rgb) => (contrast(white, c) >= contrast(text1, c) ? '#ffffff' : toHex(text1));
  return {
    c1: toHex(c1), c2: toHex(c2), c3: toHex(c3),
    track: toHex(mixRgb(ink, sheet, dark ? 0.16 : 0.11)),
    on1: onOf(c1), on2: onOf(c2), on3: onOf(c3),
    p2, p3,
  };
}

/** Οι τόνοι ως CSS μεταβλητές (μπαίνουν inline στη σελίδα· τα γραφήματα γράφουν var(--viz-1) κ.λπ.). */
export function vizVars(t: VizTones): Record<string, string> {
  return {
    '--viz-1': t.c1, '--viz-2': t.c2, '--viz-3': t.c3, '--viz-track': t.track,
    '--on-viz-1': t.on1, '--on-viz-2': t.on2, '--on-viz-3': t.on3,
  };
}
